import { useOptionalDependency } from 'ditox-react';
import { GalleryRemove } from 'iconsax-react';
import { type CSSProperties, type ImgHTMLAttributes, useState } from 'react';
import { DYNAMIC_ENDPOINT_MANAGER_TOKEN } from '@/container';
import usePreloadImage from '@/hooks/use-preload-image';
import cn from '@/utils/merge-class-names';

type Props = Omit<ImgHTMLAttributes<HTMLImageElement>, 'width' | 'height'> & {
  src: string | null | undefined;
  size?: [number, number];
  displaySize?: 'fixed' | 'auto';
  displayType?: 'img' | 'div';
  isInView?: boolean;
  onImageError?: () => void;
  onImageLoad?: (image: HTMLImageElement) => void;
};

export function SafeImage({
  src,
  size,
  alt = '',
  className,
  displayType = 'div',
  displaySize = 'auto',
  isInView = true,
  onError,
  onImageError,
  onImageLoad,
  style,
  ...props
}: Props) {
  const [hasError, setHasError] = useState(false);
  const dynamicEndpointManager = useOptionalDependency(DYNAMIC_ENDPOINT_MANAGER_TOKEN);

  const { src: safeSrc, fileEndpoint, dimension } = usePreloadImage(src, { size });

  const handleError = () => {
    dynamicEndpointManager?.revokeFileEndpoint(fileEndpoint);
    setHasError(true);
    onImageError?.();
  };

  if (hasError) {
    return (
      <div className={cn('p-2 flex flex-center bg-neutral-0', className)}>
        <GalleryRemove
          variant='Outline'
          className='size-8 text-neutral-300'
        />
      </div>
    );
  }

  if (!isInView) return null;

  const commonStyles: CSSProperties = {
    ...(displaySize === 'fixed' ? { width: dimension[0] ?? 'auto', height: dimension[1] ?? 'auto' } : {}),
    ...style,
  };

  if (displayType === 'img') {
    return (
      <img
        {...props}
        src={safeSrc}
        alt={alt}
        crossOrigin='anonymous'
        style={commonStyles}
        className={cn('bg-neutral-0 object-contain text-center', className)}
        onLoad={(event) => onImageLoad?.(event.target as HTMLImageElement)}
        onError={(event) => {
          onError?.(event);
          handleError();
        }}
      />
    );
  }

  return (
    <div
      className={cn('bg-neutral-0 bg-contain bg-center bg-no-repeat', className)}
      style={{
        ...commonStyles,
        backgroundImage: `url(${safeSrc})`,
      }}
    />
  );
}
